import { updateProfile } from 'flow/actions/profile-actions';
import Loading from 'flow/components/ui-components/Loading';
import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import Divider from 'flow/components/ui-components/Divider';
import RouteLink from 'flow/components/ui-components/RouteLink';
import SettingSelectableItem from 'flow/components/ui-components/SettingSelectableItem';
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import { ExtensionKey, ProfileKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { PriorityItems } from 'flow/utils/profile-utils';
import { generatePath } from 'flow/utils/router-utils';
import { fromJS } from 'immutable';
import React from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { useHistory, useParams } from 'react-router-dom';
import Page from '../Page';
import SettingSection from '../SettingSection';

const ProfileAvailability = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey, profileKey } = useParams<{
        extensionKey: ExtensionKey;
        profileKey: ProfileKey;
    }>();

    const { extension } = useExtension(extensionKey);
    const { getProfile } = useProfiles(extensionKey);

    const profile = getProfile(profileKey);

    if (!profile) {
        return <Loading />;
    }

    const onClickAvailability = (available: boolean) => {
        dispatch(
            updateProfile(
                extensionKey,
                fromJS({
                    key: profileKey,
                    available,
                }),
            ),
        );

        history.push(
            generatePath(routes['settings.profiles.profileKey'], {
                extensionKey,
                profileKey,
            }),
        );
    };

    return (
        <Page
            header={profile.get('description')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.profiles.profileKey'], {
                        extensionKey,
                        profileKey,
                    }),
                )
            }
        >
            <SettingSection className="text-sm/4">
                {i18next.t('status.status')}
            </SettingSection>

            <SettingSelectableItem
                onClick={onClickAvailability}
                value={true}
                selected={profile.get('available')}
            >
                <div className="flex items-center gap-2">
                    <ContactAvatar
                        contact={extension.get('contact')}
                        status="available"
                    />

                    <div>{i18next.t('status.available')}</div>
                </div>
            </SettingSelectableItem>

            <SettingSelectableItem
                onClick={onClickAvailability}
                value={false}
                selected={profile.get('available')}
            >
                <div className="flex items-center gap-2">
                    <ContactAvatar
                        contact={extension.get('contact')}
                        status="unavailable"
                    />

                    <div>{i18next.t('status.unavailable')}</div>
                </div>
            </SettingSelectableItem>

            <Divider />

            <RouteLink
                to={generatePath(
                    routes[
                        'settings.profiles.profileKey.availability.priority'
                    ],
                    {
                        extensionKey,
                        profileKey,
                    },
                )}
                description={i18next.t(
                    `settings.profile.priority.${
                        PriorityItems[profile.get('priority')].value
                    }`,
                )}
            >
                {i18next.t('settings.profile.priority')}
            </RouteLink>

            <p className="p-2 text-xs text-gray-500">
                {i18next.t('settings.profile.priority.helptext')}
            </p>
        </Page>
    );
};

export default ProfileAvailability;
